import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProjectsCarousel.css';

const ProjectsCarousel = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');
  const [userInteracted, setUserInteracted] = useState(false);
  const carouselRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  const userInteractionTimeoutRef = useRef(null);

  const projects = [
    {
      id: 1,
      title: "DGE Cyber Security Week",
      slug: "dge-cyber-security-week",
      description: "This interactive and immersive event was designed to engage employees and raise awareness about cyber security through a week-long series of creative activations. At its core was the Web Game Challenge, where a new cyber security-themed game was unlocked daily, leading to a live leaderboard competition.",
      image: require('../../asset/Viola/banner.jpg')
    },
    {
      id: 2,
      title: "ADQ Carnival",
      slug: "adq-carnival",
      description: "A vibrant corporate carnival event designed to bring together employees and stakeholders in a fun, engaging environment. The ADQ Carnival featured multiple interactive zones, entertainment activities, and team-building experiences that reinforced company culture.",
      image: require('../../asset/ADQ Carnival/1.JPG')
    },
    {
      id: 3,
      title: "Arab Health",
      slug: "arab-health",
      description: "A comprehensive healthcare exhibition showcasing the latest medical technologies, innovations, and solutions. Our activation created an immersive experience that connected healthcare professionals with cutting-edge medical equipment and services.",
      image: require('../../asset/Arab Health/1.JPEG')
    },
    {
      id: 4,
      title: "NBA Ramadan Majlis",
      slug: "nba-ramadan-majlis",
      description: "A unique cultural activation combining the excitement of NBA basketball with traditional Ramadan majlis hospitality. This special event created a bridge between international sports culture and local traditions during the holy month of Ramadan.",
      image: require('../../asset/NBA Ramadan Majlis/1.jpg')
    },
    {
      id: 5,
      title: "Global Freight Summit",
      slug: "global-freight-summit",
      description: "An energetic, culturally resonant experience that promoted competition and engagement, reflecting the excitement of sport and achievement within the logistics and freight industry. Featured an Interactive Cricket Simulation with Live Leaderboard that attracted consistent crowds and fostered friendly competition.",
      image: require('../../asset/GPJ/1.jpg')
    },
    {
      id: 6,
      title: "NBA BRED Festival",
      slug: "nba-bred-festival",
      description: "Dynamic basketball-themed interactive games that combined physical activity, reflex challenges, and arcade-style fun, closely tied to the NBA brand and culture. Featured Basketball Drop and Catch Reflex Game and Basketball Shoot Game on LED Wall Screen.",
      image: require('../../asset/NBA BRED/1.jpg')
    }
  ];

  const handleUserInteraction = () => {
    setUserInteracted(true);
    
    // Clear existing auto-advance
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    
    // Clear existing user interaction timeout
    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
    }
    
    // Resume auto-advance after 10 seconds of no interaction
    userInteractionTimeoutRef.current = setTimeout(() => {
      setUserInteracted(false);
    }, 10000);
  };

  const nextProject = (isAutoAdvance = false) => {
    if (isAnimating) return;
    
    if (!isAutoAdvance) {
      handleUserInteraction();
    }
    
    const next = (currentIndex + 1) % projects.length;
    setSlideDirection('right'); // Next slide comes from right
    setNextIndex(next);
    setIsAnimating(true);
    
    setTimeout(() => {
      setCurrentIndex(next);
      setIsAnimating(false);
    }, 400);
  };

  const prevProject = () => {
    if (isAnimating) return;
    
    handleUserInteraction();
    
    const prev = (currentIndex - 1 + projects.length) % projects.length;
    setSlideDirection('left'); // Previous slide comes from left
    setNextIndex(prev);
    setIsAnimating(true);
    
    setTimeout(() => {
      setCurrentIndex(prev);
      setIsAnimating(false);
    }, 400);
  };

  // Smart auto-advance that respects user interaction
  useEffect(() => {
    if (!userInteracted) {
      autoAdvanceRef.current = setInterval(() => {
        nextProject(true); // Pass true to indicate this is auto-advance
      }, 5000);
    }

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [userInteracted, currentIndex]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
      }
    };
  }, []);



  return (
    <section className="projects-carousel">
      <h2 className="carousel-heading">
        Our Projects
      </h2>
      
      <div className="carousel-container" ref={carouselRef}>
        <div className="slide-wrapper">
          {/* Current slide */}
          <div className={`carousel-slide current-slide ${isAnimating ? `slide-out-${slideDirection}` : ''}`}>
            <div className="project-card">
              <div className="project-image-container">
                <img 
                  src={projects[currentIndex].image} 
                  alt={projects[currentIndex].title} 
                  className="project-image"
                />
              </div>
              
              <div className="project-content">
                <h3 className="project-title" style={{ color: 'white' }}>
                  {projects[currentIndex].title}
                </h3>
                <p className="project-description">
                  {projects[currentIndex].description}
                </p>
                <button 
                  className="project-btn"
                  onClick={() => navigate(`/project/${projects[currentIndex].slug}`)}
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
          
          {/* Next slide - only visible during animation */}
          {isAnimating && (
            <div className={`carousel-slide next-slide slide-in-${slideDirection}`}>
              <div className="project-card">
                <div className="project-image-container">
                  <img 
                    src={projects[nextIndex].image} 
                    alt={projects[nextIndex].title} 
                    className="project-image"
                  />
                </div>
                
                <div className="project-content">
                  <h3 className="project-title" style={{ color: 'white' }}>
                    {projects[nextIndex].title}
                  </h3>
                  <p className="project-description">
                    {projects[nextIndex].description}
                  </p>
                  <button 
                    className="project-btn"
                    onClick={() => navigate(`/project/${projects[nextIndex].slug}`)}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="carousel-controls">
          <div className="carousel-dots">
            {projects.map((_, index) => (
              <span 
                key={index} 
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  if (!isAnimating && index !== currentIndex) {
                    handleUserInteraction();
                    
                    const direction = index > currentIndex ? 'right' : 'left';
                    setSlideDirection(direction);
                    setNextIndex(index);
                    setIsAnimating(true);
                    
                    setTimeout(() => {
                      setCurrentIndex(index);
                      setIsAnimating(false);
                    }, 400);
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
      
      <div className="view-all-container">
        <button 
          className="view-all-btn"
          onClick={() => window.location.href = '/projects'}
        >
          View All Projects
        </button>
      </div>
    </section>
  );
};

export default ProjectsCarousel; 